/**
 * useClubQuery Hook
 * 
 * React Query hook for fetching club data with tenant information.
 * Automatically uses clubId from appRuntime.
 * 
 * @example
 * ```typescript
 * function ClubInfo() {
 *   const { data: club, isLoading, error } = useClubQuery();
 *   
 *   if (isLoading) return <div>Loading club...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *   if (!club) return null;
 *   
 *   return (
 *     <div>
 *       <h1>{club.name}</h1>
 *       <p>Timezone: {club.timezone}</p>
 *       <p>Tenant: {club.tenant.handle}</p>
 *     </div>
 *   );
 * }
 * ```
 */

import { useQuery } from '@tanstack/react-query';
import { useClubId } from '~/services/appRuntime';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { getClubById } from '../api/clubService';
import { useClubQueryError } from './useClubQueryError';
import type { ClubWithTenant } from '../types';

/**
 * Hook to fetch club data
 * 
 * **Important**: Query is disabled when clubId is not available.
 * The query key will be `['club', 'disabled']` when disabled to avoid
 * cache pollution with invalid IDs like 0 or -1.
 * 
 * **Query Keys**: Uses centralized query key factory from `~/lib/query/keys`
 * with club-scoped pattern: `['clubId', clubId, 'club', 'detail']`
 * 
 * **Enhanced Error Handling**: Returns processed error with user-friendly messages.
 * Components can use `clubQueryError` to display errors without additional parsing.
 * 
 * **Smart Retry Logic**:
 * - Automatically retries connectivity errors (network/server) up to 3 times
 * - Uses exponential backoff (1s, 2s, 4s)
 * - Does NOT retry validation errors (user action required)
 * 
 * @returns React Query result with club data and processed error
 * 
 * @example
 * ```tsx
 * function ClubInfo() {
 *   const { data: club, isLoading, clubQueryError } = useClubQuery();
 *   
 *   if (clubQueryError) {
 *     return (
 *       <BookingError
 *         title={clubQueryError.title}
 *         message={clubQueryError.detailedMessage}
 *         action={clubQueryError.onRetry ? {
 *           label: 'Retry',
 *           onClick: clubQueryError.onRetry
 *         } : undefined}
 *       />
 *     );
 *   }
 *   
 *   // ... rest of component
 * }
 * ```
 */
export function useClubQuery() {
  const [clubId] = useClubId();
  const clubIdNumber = clubId ? Number(clubId) : null;

  const query = useQuery<ClubWithTenant, Error>({
    // Use centralized query keys with club-scoped pattern
    queryKey: clubIdNumber ? queryKeys.club.detail(clubIdNumber) : queryKeys.disabled.club(),
    queryFn: () => {
      if (!clubIdNumber) {
        throw new Error('Club ID is required');
      }
      return getClubById(clubIdNumber);
    },
    enabled: !!clubIdNumber, // Only fetch if clubId exists and is valid
    // Stable data - club details rarely change mid-session
    ...cachePresets.stable,
    
    // Smart retry logic: only retry connectivity errors
    retry: (failureCount, error) => {
      // Don't retry validation errors (422) - these require user/admin action
      if (error.message?.includes('missing pricing') || 
          error.message?.includes('missing operating hours')) {
        return false;
      }
      
      // Retry connectivity errors up to 3 times
      // Network errors and server errors (5xx) are likely transient
      const shouldRetry = failureCount < 3;
      
      if (import.meta.env.DEV && shouldRetry) {
        console.log(`🔄 [useClubQuery] Retry attempt ${failureCount + 1}/3 for error:`, error.message);
      }
      
      return shouldRetry;
    },
    
    // Exponential backoff: 1s, 2s, 4s
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 4000),
  });

  // Process error with enhanced error handling
  const clubQueryError = useClubQueryError(query.error, query.refetch);

  return {
    ...query,
    clubQueryError, // Expose processed error for easier component handling
  };
}
